import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Table, Collapse } from "antd";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import moment from "moment";
import Select from "react-select";
import CompanyModelPopup from "../../../components/modelpopup/CompanyModelPopup";
import SearchBox from "../../../components/SearchBox";
import { useNotification } from "../../context/NotificationContext";
const { Panel } = Collapse;
const SubscribedCompany = () => {
  const [companies, setCompanies] = useState([]);
  const [groupedCompanies, setGroupedCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [partners, setPartners] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [appAccessFilter, setAppAccessFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState(null);
  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");
  const dateFormat = localStorage.getItem("dateFormat") || "MMM DD, YYYY";
  const [isFullScreen, setFullScreen] = useState(false);
  const maximizeBtnRef = useRef(null);
    const { notify } = useNotification();

  const appAccessOptions = [
    { value: "hms", label: "HMS" },
    { value: "office", label: "Office" },
    { value: "pos", label: "POS" },
    { value: "digital-menu", label: "DIGITAL MENU" },
  ];
  const statusOptions = [
    { value: "expired", label: "Expired" },
    { value: "running", label: "Running" },
  ];
  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${apiUrl}/company/list`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCompanies(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching companies:", error);
      notify("error","Failed to fetch companies. Please try again later.");
      setLoading(false);
    }
  };
  const fetchPartners = async () => {
    try {
      const response = await axios.get(`${apiUrl}/partners/list`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const partnerOptions = response.data.map((partner) => ({
        value: partner.partner_id,
        label: partner.company_name,
      }));
      setPartners(partnerOptions);
    } catch (error) {
      console.error("Error fetching partners:", error);
    }
  };
  useEffect(() => {
    if (apiUrl && token) {
      fetchCompanies();
      fetchPartners();
    }
  }, [apiUrl, token]);
  useEffect(() => {
    const handleClick = () => {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen();
        setFullScreen(true);
      } else {
        if (document.exitFullscreen) {
          document.exitFullscreen();
          setFullScreen(false);
        }
      }
    };
    const cleanup = () => {
      if (isFullScreen && document.exitFullscreen) {
        document.exitFullscreen();
        setFullScreen(false);
      }
    };
    const maximizeBtn = maximizeBtnRef.current;
    maximizeBtn.addEventListener("click", handleClick);
    return () => {
      maximizeBtn.removeEventListener("click", handleClick);
      cleanup();
    };
  }, [isFullScreen]);
  // Group companies by parent_companyid
  useEffect(() => {
    const groupCompanies = () => {
      const parentCompanies = companies.filter((c) => c.parent_companyid === 0 || c.parent_companyid === null);
      const grouped = parentCompanies.map((parent) => ({
        ...parent,
        children: companies.filter((c) => c.parent_companyid === parent.companyid),
      }));
      // Include companies with non-zero parent_companyid that don't have a matching parent
      const standaloneCompanies = companies.filter(
        (c) => c.parent_companyid !== 0 && c.parent_companyid !== null && !companies.some((p) => p.companyid === c.parent_companyid)
      );
      setGroupedCompanies([...grouped, ...standaloneCompanies.map((c) => ({ ...c, children: [] }))]);
    };
    groupCompanies();
  }, [companies]);
  const handleSave = (company) => {
    if (isEditing) {
      setCompanies((prevCompanies) =>
        prevCompanies.map((c) => (c.companyid === company.companyid ? company : c))
      );
    } else {
      setCompanies((prevCompanies) => [...prevCompanies, company]);
    }
    setIsEditing(false);
    setSelectedCompany(null);
    setIsModalOpen(false);
    fetchCompanies();
  };
  const handleAddClick = (e) => {
    e.preventDefault();
    setIsEditing(false);
    setSelectedCompany(null);
    setIsModalOpen(true);
  };
  const handleEditClick = (e, company) => {
    e.preventDefault();
    setSelectedCompany(company);
    setIsEditing(true);
    setIsModalOpen(true);
  };
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedCompany(null);
    setIsEditing(false);
  };
  const handleDeleteClick = async (companyId) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You will not be able to recover this company!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
      reverseButtons: true,
    });
    if (result.isConfirmed) {
      const validation = await Swal.fire({
        title: "Confirm Deletion",
        text: "Please type 'netshine' to confirm deletion.",
        input: "text",
        inputPlaceholder: "Type 'netshine' here",
        showCancelButton: true,
        confirmButtonText: "Confirm",
        cancelButtonText: "Cancel",
        inputValidator: (value) => {
          if (value !== "netshine") {
            return "You need to type 'netshine' to confirm!";
          }
        },
      });
      if (validation.isConfirmed) {
        setLoading(true);
        try {
          await axios.delete(`${apiUrl}/company/delete/${companyId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setCompanies((prevCompanies) =>
            prevCompanies.filter((company) => company.companyid !== companyId)
          );
          notify("success","Company has been deleted.");
        } catch (error) {
          let errorMessage = "Failed to delete company. Please try again later.";
          if (error.response && error.response.data && error.response.data.error) {
            errorMessage = error.response.data.error;
          }
          Swal.fire("Error!", errorMessage, "error");
        } finally {
          setLoading(false);
          fetchCompanies();
        }
      }
    }
  };
  const handleStatusToggle = async (e, company) => {
    e.preventDefault();
    const newStatus = company.status === 1 ? 0 : 1;
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `You are about to ${newStatus === 1 ? "activate" : "deactivate"} this company.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, change it!",
      cancelButtonText: "No, cancel!",
      reverseButtons: true,
    });
    if (result.isConfirmed) {
      try {
        const response = await axios.put(
          `${apiUrl}/company/update/${company.companyid}`,
          { status: newStatus },
          { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
        );
        setCompanies((prevCompanies) =>
          prevCompanies.map((c) =>
            c.companyid === company.companyid ? { ...c, status: response.data.status } : c
          )
        );
        await Swal.fire("Updated!", `Company has been ${newStatus === 1 ? "activated" : "deactivated"}.`, "success");
        fetchCompanies();
      } catch (error) {
        let errorMessage = "Failed to update company status. Please try again later.";
        if (error.response && error.response.data && error.response.data.error) {
          errorMessage = error.response.data.error;
        }
        await Swal.fire("Error!", errorMessage, "error");
      }
    }
  };
  const handleResetAccess = async (companyId) => {
    const result = await Swal.fire({
      title: "Reset Access Options",
      text: "Choose an action for resetting access:",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Generate Password",
      cancelButtonText: "Send Reset Link",
      showDenyButton: true,
      denyButtonText: "Cancel",
    });
    if (result.isConfirmed) {
      try {
        const response = await axios.post(
          `${apiUrl}/company/reset-access/${companyId}`,
          { companyid: companyId, action: "generate" },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const newPassword = response.data.newPassword;
        await Swal.fire({
          title: "Password Generated",
          html: `
            <p>Your new password is: <strong>${newPassword}</strong></p>
            <p>Copy it and save it securely. This password is ready to use.</p>
          `,
          icon: "success",
          showConfirmButton: true,
          confirmButtonText: "OK",
        });
      } catch (error) {
        Swal.fire("Error!", error.response?.data?.error || "Failed to reset access.", "error");
      }
    } else if (result.dismiss === Swal.DismissReason.cancel) {
      try {
        await axios.post(
          `${apiUrl}/company/reset-access/${companyId}`,
          { companyid: companyId, action: "send_link" },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        Swal.fire("Success!", "Reset link sent to the user.", "success");
      } catch (error) {
        Swal.fire("Error!", error.response?.data?.error || "Failed to send reset link.", "error");
      }
    }
  };
  const handlePartnerFilterChange = (selectedOption) => {
    setSelectedPartner(selectedOption);
  };
  const calculatePlanEndDate = (startDate, cycle) => {
    if (!startDate) return "N/A";
    const date = moment(startDate);
    if (!date.isValid()) return "Invalid Date";
    if (cycle === "monthly") {
      date.add(30, "days");
    } else if (cycle === "yearly") {
      date.add(1, "year");
    }
    return date.format(dateFormat);
  };
  const isExpired = (startDate, cycle) => {
    if (!startDate) return false;
    const endDate = moment(startDate);
    if (cycle === "monthly") {
      endDate.add(30, "days");
    } else if (cycle === "yearly") {
      endDate.add(1, "year");
    }
    return moment().isAfter(endDate);
  };
  const filteredGroupedCompanies = groupedCompanies.filter((company) => {
    const matchesSearch =
      (company.company || "").toLowerCase().includes(searchText.toLowerCase()) ||
      (company.contactperson || "").toLowerCase().includes(searchText.toLowerCase()) ||
      company.children.some(
        (child) =>
          (child.company || "").toLowerCase().includes(searchText.toLowerCase()) ||
          (child.contactperson || "").toLowerCase().includes(searchText.toLowerCase())
      );
    const matchesPartner = selectedPartner
      ? company.partner_id === selectedPartner.value ||
        company.children.some((child) => child.partner_id === selectedPartner.value)
      : true;
    const matchesAppAccess =
      appAccessFilter.length > 0
        ? appAccessFilter.every((filter) => company.app_access?.includes(filter.value)) ||
          company.children.some((child) =>
            appAccessFilter.every((filter) => child.app_access?.includes(filter.value))
          )
        : true;
    const matchesStatus = statusFilter
      ? (statusFilter.value === "expired"
          ? isExpired(company.plandate, company.billingcycle)
          : !isExpired(company.plandate, company.billingcycle)) ||
        company.children.some((child) =>
          statusFilter.value === "expired"
            ? isExpired(child.plandate, child.billingcycle)
            : !isExpired(child.plandate, child.billingcycle)
        )
      : true;
    return matchesSearch && matchesPartner && matchesAppAccess && matchesStatus;
  });
  const columns = [
    {
      title: "Company",
      dataIndex: "company",
      sorter: (a, b) => (a.company || "").localeCompare(b.company || ""),
    },
    {
      title: "Contact Person",
      dataIndex: "contactperson",
      sorter: (a, b) => (a.contactperson || "").localeCompare(b.contactperson || ""),
    },
    {
      title: "Phone",
      dataIndex: "phone",
      sorter: (a, b) => (a.phone || "").localeCompare(b.phone || ""),
    },
    {
      title: "Email",
      dataIndex: "email",
      sorter: (a, b) => (a.email || "").localeCompare(b.email || ""),
    },
    {
      title: "Current Plan",
      dataIndex: "currentplan",
      render: (plan) => (
        <span>
          {plan === 1 ? "Trial" : plan === 2 ? "Professional" : plan === 3 ? "Enterprise" : "Unknown"}
        </span>
      ),
      sorter: (a, b) => a.currentplan - b.currentplan,
    },
    {
      title: "Cycle",
      dataIndex: "billingcycle",
      render: (cycle) => <span>{cycle === "monthly" ? "Monthly" : "Yearly"}</span>,
      sorter: (a, b) => (a.billingcycle || "").localeCompare(b.billingcycle || ""),
    },
    {
      title: "Plan Start Date",
      dataIndex: "plandate",
      render: (date) => (date ? moment(date).format(dateFormat) : "N/A"),
      sorter: (a, b) => new Date(a.plandate) - new Date(b.plandate),
    },
    {
      title: "Plan End Date",
      render: (_, record) => <span>{calculatePlanEndDate(record.plandate, record.billingcycle)}</span>,
      sorter: (a, b) =>
        new Date(calculatePlanEndDate(a.plandate, a.billingcycle)) -
        new Date(calculatePlanEndDate(b.plandate, b.billingcycle)),
    },
    {
      title: "Locations",
      dataIndex: "locationallowed",
      sorter: (a, b) => a.locationallowed - b.locationallowed,
    },
    {
      title: "App Access",
      dataIndex: "app_access",
      render: (app_access) => <span>{app_access ? app_access.join(", ") : "N/A"}</span>,
      sorter: (a, b) => (a.app_access || []).length - (b.app_access || []).length,
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status, record) => (
        <div className="dropdown action-label">
          <button
            className={`btn btn-white btn-sm btn-rounded dropdown-toggle ${status === 1 ? "text-success" : "text-danger"}`}
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            <i className={`far fa-dot-circle ${status === 1 ? "text-success" : "text-danger"}`} />{" "}
            {status === 1 ? "Active" : "Inactive"}
          </button>
          <div className="dropdown-menu">
            <button
              className="dropdown-item"
              onClick={(e) => handleStatusToggle(e, record)}
            >
              <i className={`far fa-dot-circle ${status === 1 ? "text-danger" : "text-success"}`} />{" "}
              {status === 1 ? "Deactivate" : "Activate"}
            </button>
          </div>
        </div>
      ),
    },
    {
      title: "Action",
      render: (_, record) => (
        <div className="dropdown dropdown-action text-end">
          <Link to="#" className="action-icon dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
            <i className="material-icons">more_vert</i>
          </Link>
          <div className="dropdown-menu dropdown-menu-right">
            <Link className="dropdown-item" to="#">
              <i className="fa fa-eye m-r-5" /> View
            </Link>
            <Link
              className="dropdown-item"
              to="#"
              onClick={(e) => handleEditClick(e, record)}
            >
              <i className="fa fa-pencil m-r-5" /> Edit
            </Link>
            <Link
              className="dropdown-item"
              to="#"
              onClick={() => handleDeleteClick(record.companyid)}
            >
              <i className="fa fa-trash m-r-5" /> Delete
            </Link>
            <Link
              className="dropdown-item"
              to="#"
              onClick={() => handleResetAccess(record.companyid)}
            >
              <i className="fa fa-key m-r-5" /> Reset Access
            </Link>
          </div>
        </div>
      ),
      sorter: true,
    },
  ];
  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <div className="page-header">
          <div className="row align-items-center">
            <div className="col-md-3">
              <SearchBox
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search by Company or Contact"
              />
            </div>
            <div className="col-md-3">
              <Select
                options={partners}
                placeholder="Filter by Partner"
                onChange={handlePartnerFilterChange}
                isClearable
              />
            </div>
            <div className="col-md-3">
              <Select
                options={appAccessOptions}
                placeholder="Filter by App Access"
                onChange={setAppAccessFilter}
                isMulti
              />
            </div>
            <div className="col-md-3">
              <Select
                options={statusOptions}
                placeholder="Filter by Status"
                onChange={setStatusFilter}
                isClearable
              />
            </div>
            <div className="col-md-12 float-end ms-auto">
              <div className="d-flex title-head">
                <div className="view-icons">
                  <Link to="#" className="grid-view btn btn-link">
                    <i className="las la-redo-alt" onClick={fetchCompanies} />
                  </Link>
                  <Link to="#" className="list-view btn btn-link" id="collapse-header" ref={maximizeBtnRef}>
                    <i className="las la-expand-arrows-alt" />
                  </Link>
                </div>
                <Link to="#" className="btn add-btn" onClick={handleAddClick}>
                  <i className="la la-plus-circle" /> Add New
                </Link>
              </div>
            </div>
          </div>
        </div>
        <div className="row mt-3">
          <div className="col-md-12">
            <div className="table-responsive">
              <Collapse
                accordion
                style={{ backgroundColor: "#fff", borderRadius: "4px" }}
                expandIconPosition="right"
              >
                {filteredGroupedCompanies.map((company) => {
                  const hasChildren = company.children.length > 0;
                  const headerContent = (
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span>
                        <strong>{company.company}</strong>
                        {company.contactperson && ` - ${company.contactperson}`}
                        {company.phone && ` - ${company.phone}`}
                        {company.email && ` - ${company.email}`}
                        {hasChildren && ` (${company.children.length})`}
                      </span>
                      <div className="dropdown dropdown-action">
                        <Link
                          to="#"
                          className="action-icon dropdown-toggle"
                          data-bs-toggle="dropdown"
                          aria-expanded="false"
                        >
                          <i className="material-icons">more_vert</i>
                        </Link>
                        <div className="dropdown-menu dropdown-menu-right">
                          <Link className="dropdown-item" to="#">
                            <i className="fa fa-eye m-r-5" /> View
                          </Link>
                          <Link
                            className="dropdown-item"
                            to="#"
                            onClick={(e) => handleEditClick(e, company)}
                          >
                            <i className="fa fa-pencil m-r-5" /> Edit
                          </Link>
                          <Link
                            className="dropdown-item"
                            to="#"
                            onClick={() => handleDeleteClick(company.companyid)}
                          >
                            <i className="fa fa-trash m-r-5" /> Delete
                          </Link>
                          <Link
                            className="dropdown-item"
                            to="#"
                            onClick={() => handleResetAccess(company.companyid)}
                          >
                            <i className="fa fa-key m-r-5" /> Reset Access
                          </Link>
                        </div>
                      </div>
                    </div>
                  );
                  const tableContent = (
                    <Table
                      className="table-striped"
                      style={{ overflowX: "auto" }}
                      columns={columns}
                      dataSource={hasChildren ? [company, ...company.children] : [company]}
                      rowKey={(record) => record.companyid}
                      pagination={false}
                      locale={{ emptyText: "No companies found" }}
                      rowClassName={(record, index) => (index === 0 ? "parent-company-row" : "")}
                    />
                  );
                  if (hasChildren) {
                    return (
                      <Panel
                        key={company.companyid}
                        header={headerContent}
                        style={{ marginBottom: "8px" }}
                      >
                        {tableContent}
                      </Panel>
                    );
                  } else {
                    return (
                      <div
                        key={company.companyid}
                        style={{
                          marginBottom: "8px",
                          backgroundColor: "#fff",
                          borderRadius: "4px",
                          border: "1px solid #e8e8e8",
                        }}
                      >
                        <div
                          style={{
                            padding: "10px 16px",
                            borderBottom: "1px solid #e8e8e8",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          {headerContent}
                        </div>
                        <div style={{ padding: "16px" }}>
                          {tableContent}
                        </div>
                      </div>
                    );
                  }
                })}
              </Collapse>
            </div>
          </div>
        </div>
      </div>
      {isModalOpen && (
        <CompanyModelPopup
          onSave={handleSave}
          company={selectedCompany}
          isEditing={isEditing}
          partners={partners}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};
export default SubscribedCompany;